/**
 * Transaction Guard Store
 *
 * Zustand store that manages the transaction confirmation flow.
 * Uses a "promise bridge" pattern to pause signTransaction() until
 * the user confirms or cancels in the TransactionConfirmationDialog.
 */

import { create } from "zustand";
import type { Transaction } from "@solana/kit";
import type { DecodedTransaction } from "../lib/transaction-decoder";

const CONFIRMATION_TIMEOUT_MS = 60_000;

interface TransactionGuardState {
  pendingTransaction: Transaction | null;
  decodedTransaction: DecodedTransaction | null;
  resolver: { resolve: () => void; reject: (err: Error) => void } | null;

  requestConfirmation: (
    tx: Transaction,
    decoded: DecodedTransaction,
  ) => Promise<void>;
  confirmTransaction: () => void;
  rejectTransaction: () => void;
}

export const useTransactionGuardStore = create<TransactionGuardState>()(
  (set, get) => ({
    pendingTransaction: null,
    decodedTransaction: null,
    resolver: null,

    requestConfirmation: (tx, decoded) => {
      // Reject any existing pending transaction
      const existing = get().resolver;
      if (existing) {
        existing.reject(new Error("Superseded by new transaction"));
      }

      let timer: ReturnType<typeof setTimeout>;

      return Promise.race([
        new Promise<void>((resolve, reject) => {
          set({
            pendingTransaction: tx,
            decodedTransaction: decoded,
            resolver: { resolve, reject },
          });
        }),
        new Promise<never>((_, reject) => {
          timer = setTimeout(
            () => reject(new Error("Transaction confirmation timed out")),
            CONFIRMATION_TIMEOUT_MS,
          );
        }),
      ]).finally(() => {
        clearTimeout(timer);
        set({
          pendingTransaction: null,
          decodedTransaction: null,
          resolver: null,
        });
      });
    },

    confirmTransaction: () => {
      const { resolver } = get();
      if (resolver) {
        resolver.resolve();
      }
    },

    rejectTransaction: () => {
      const { resolver } = get();
      if (resolver) {
        resolver.reject(new Error("Transaction rejected by user"));
      }
    },
  }),
);
